import { useEffect, useState } from 'react';
import { apiRequest } from '../config/api';
import { normalizeIpForCompare } from '../internalStaffIps';

interface ClientIpResponse {
  client_ip: string;
}

/**
 * Fetches the caller's public IP from the admin API once per JWT (same extraction as analytics writes).
 * Returns normalized IP, or null if unknown / error. While the first request is in flight, `viewerIp` is undefined.
 */
export function useAdminViewerIp(token: string | null | undefined): string | null | undefined {
  const [viewerIp, setViewerIp] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!token) {
      setViewerIp(undefined);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await apiRequest<ClientIpResponse>('/admin/session/client-ip', token);
        if (cancelled) return;
        const raw = res.client_ip?.trim() ?? '';
        if (!raw || raw.toLowerCase() === 'unknown') {
          setViewerIp(null);
          return;
        }
        setViewerIp(normalizeIpForCompare(raw));
      } catch {
        if (!cancelled) setViewerIp(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return viewerIp;
}
